import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Avatar, Box, Button, useTheme } from "@mui/material"

import config from "config"
import { gtmPushDataLayerSelectLanguage } from "utils/googletagmanager"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useHome } from "../../hooks/home/useHome"
import { useHomeMap } from "hooks/home/useHomeMap"
import { useGetUserLocationView } from "hooks/map"
import { convertMapViewObjectToPath } from "utils/map"

import { FullScreenOverlayLayout } from "layouts/FullScreenOverlayLayout"
import { QRCode } from "components/QRCode"
import { Head } from "components/Head"
import { Image } from "components/Image"
import { Q_MAP_VIEW } from "constant"
import { compact } from "lodash"
import { getIcon } from "utils/icon"
import { NearMeIcon } from "components/icons"
import { HomeActionButton } from "components/domains/home/HomeActionButton"

export const HomePage = () => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { venues, defaultRoutePath } = useVenue()
  const { locales } = useHome()
  const userLocationView = useGetUserLocationView()

  useHomeMap(venues)

  const styleOverrides = theme.components[`HomePage`]?.styleOverrides || {}
  const {
    root,
    contentContainer,
    welcomePanelWrapper,
    welcomeContainer,
    localeListContainer,
    localeButton,
    actionContainer,
    actionButton,
    logoContainer,
    logoImage,
    qrContainer,
    qrCode,
    screensaverContainer,
    homeActionButton,
    carouselWrapper,
  } = styleOverrides
  const { url: logoUrl } = theme?.assets?.homeLogo || theme?.assets?.logo || {}

  const navigationIcon = useMemo(
    () => getIcon(theme?.assets?.home?.navigation) || <NearMeIcon />,
    [theme]
  )

  const switchLanguage = useCallback(
    (lang) => (e) => {
      i18n.changeLanguage(lang)
      gtmPushDataLayerSelectLanguage(lang)
    },
    [i18n]
  )
  const mobileBaseURL = config("mobileBaseUrl")
  const currentLanguage = useMemo(() => i18n.language, [i18n.language])

  const handleClick = useCallback(() => {
    const userLocationViewPath = convertMapViewObjectToPath(userLocationView)
    const [path, prevQs = ""] = defaultRoutePath.split("?")
    const qs = compact([
      prevQs,
      userLocationViewPath ? `${Q_MAP_VIEW}=${userLocationViewPath}` : null,
    ]).join("&")
    navigate(`${path}?${qs}`)
  }, [navigate, defaultRoutePath, userLocationView])

  return (
    <>
      <Head title={t("Indoor map")} />
      <FullScreenOverlayLayout sx={root}>
        <Box sx={contentContainer}>
          <Box sx={screensaverContainer}>
            <Box sx={carouselWrapper}>
              {/** To show screensaver, use KioskHApp preservedView instead */}
            </Box>
          </Box>
          <Box sx={welcomePanelWrapper}>
            <Box sx={welcomeContainer}>
              <Box sx={logoContainer}>
                <Image src={logoUrl} sx={logoImage} />
              </Box>
              <Box sx={localeListContainer}>
                {locales.map((locale) => {
                  const isCurrentLanguage =
                    currentLanguage === locale.localeKey.toString()
                  return (
                    <Button
                      key={locale.localeKey}
                      onClick={switchLanguage(locale.localeKey)}
                      sx={{
                        ...localeButton,
                        opacity: isCurrentLanguage ? 1 : 0.5,
                      }}
                    >
                      <Avatar
                        src={`https://flagcdn.com/h40/${locale.flagCode}.webp`}
                      />
                    </Button>
                  )
                })}
              </Box>
              <Box sx={actionContainer}>
                <Button
                  sx={actionButton}
                  variant="contained"
                  onClick={handleClick}
                >
                  {t("Touch to start")}
                </Button>
                <HomeActionButton
                  className="Mui-selected highlight"
                  sx={homeActionButton}
                  icon={navigationIcon}
                  onClick={handleClick}
                  primary={t("Start Explore Primary Label")}
                  secondary={t("Start Explore Secondary Label")}
                />
              </Box>
              {mobileBaseURL && (
                <Box sx={qrContainer}>
                  <QRCode
                    url={mobileBaseURL}
                    reactQRCodeProps={{ bgColor: "transparent" }}
                    sx={qrCode}
                    caption={t("Scan to get navigation map on your phone")}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </FullScreenOverlayLayout>
    </>
  )
}
